import flatMap from 'lodash/flatMap';
import { ContainerItem } from '../../container';
import {
  isVolumeClaimTemplateItem,
  isVolumeKeymapItem,
  isVolumeStorageItem,
  VolumeItem,
  VolumeKeymapItem,
  VolumeKeymapTab,
  VolumeMountItem,
  VolumeMountTab,
  VolumeMountType,
  VolumeStorageItem,
} from '../../storage-settings';
import {
  YamlVolumeConfigMapItem,
  YamlVolumeHostPathItem,
  YamlVolumeMountItem,
  YamlVolumePersistenceItem,
  YamlVolumeSecretItem,
  YamlVolumeTempItem,
} from '../../types';

/** 将卷转换成 yaml 格式 */
export const volumesToYaml = (volumes: VolumeItem[]): YamlVolumeMountItem[] => {
  const filteredVolumes = volumes?.filter((volume) => {
    return !isVolumeClaimTemplateItem(volume);
  });
  return filteredVolumes?.map((volume) => {
    // 处理存储类（持久卷/临时卷/HostPath）
    if (isVolumeStorageItem(volume)) {
      const storageItem = volume as VolumeStorageItem;

      switch (storageItem.type) {
        case VolumeMountTab.PERSISTENCE: {
          const item: YamlVolumePersistenceItem = {
            name: storageItem.volumeName,
            persistentVolumeClaim: { claimName: storageItem.selected },
          };
          return item;
        }

        case VolumeMountTab.TEMPORARY: {
          const item: YamlVolumeTempItem = {
            name: storageItem.volumeName,
            emptyDir: {},
          };
          return item;
        }

        case VolumeMountTab.HOST_PATH: {
          const item: YamlVolumeHostPathItem = {
            name: storageItem.volumeName,
            hostPath: { path: storageItem.volumeHostPath },
          };
          return item;
        }
        default:
          return undefined;
      }
    }
    // 处理键映射类（ConfigMap/SecretMap）
    else if (isVolumeKeymapItem(volume)) {
      const keymapItem = volume as VolumeKeymapItem;

      switch (keymapItem.type) {
        case VolumeKeymapTab.CONFIG_MAP: {
          const item: YamlVolumeConfigMapItem = {
            name: keymapItem.volumeName,
            configMap: {
              name: keymapItem.selected,
              items: keymapItem.items,
            },
          };
          return item;
        }

        case VolumeKeymapTab.SECRET_MAP: {
          const item: YamlVolumeSecretItem = {
            name: keymapItem.volumeName,
            secret: {
              secretName: keymapItem.selected,
              items: keymapItem.items,
            },
          };
          return item;
        }
        default:
          return undefined;
      }
    }
  });
};

/** yaml 转挂载卷 */
export const yamlToVolumes = (
  /** yaml state 原始数据里面的复杂对象 */
  yamlVolumes: YamlVolumeMountItem[] = [],
  /** state 容器数组,用于参照(state 数据结构是自定义的,有利于状态可预知) */
  allContainters: ContainerItem[] = [],
): VolumeItem[] => {
  // 根据 volumeName 从 yamlContainerVolumes 中过滤出对应的挂载项，并转换为 VolumeMountItem
  const getVolumeMounts = (volumeName: string): VolumeMountItem[] => {
    return flatMap(allContainters, (container) => {
      return (container.volumeMounts || [])
        .filter((vm) => vm.name === volumeName)
        .map((vm) => ({
          containerName: container.containerSetting.containerName,
          mountType: vm.readOnly ? VolumeMountType.ReadOnly : VolumeMountType.ReadWrite,
          mountPath: vm.mountPath,
        }));
    });
  };

  return yamlVolumes
    ?.map((item) => {
      // 处理持久卷
      if ('persistentVolumeClaim' in item) {
        const result: VolumeStorageItem = {
          type: VolumeMountTab.PERSISTENCE,
          selected: item.persistentVolumeClaim.claimName,
          volumeName: item.name,
          volumeHostPath: undefined, // 持久卷不需要 hostPath
          volumeMounts: getVolumeMounts(item.name),
        };
        return result;
      }
      // 处理临时卷
      if ('emptyDir' in item) {
        const result: VolumeStorageItem = {
          type: VolumeMountTab.TEMPORARY,
          selected: undefined, // 临时卷无需选择
          volumeName: item.name,
          volumeHostPath: undefined, // 临时卷不需要 hostPath
          volumeMounts: getVolumeMounts(item.name),
        };
        return result;
      }
      // 处理 HostPath 卷
      if ('hostPath' in item) {
        const result: VolumeStorageItem = {
          type: VolumeMountTab.HOST_PATH,
          selected: undefined, // HostPath 不需要选择卷
          volumeName: item.name,
          volumeHostPath: item.hostPath.path,
          volumeMounts: getVolumeMounts(item.name),
        };
        return result;
      }
      // 处理 ConfigMap
      if ('configMap' in item) {
        const result: VolumeKeymapItem = {
          type: VolumeKeymapTab.CONFIG_MAP,
          selected: item.configMap.name,
          volumeName: item.name,
          items: item.configMap.items || [],
          volumeMounts: getVolumeMounts(item.name),
        };
        return result;
      }
      // 处理 SecretMap
      if ('secret' in item) {
        const result: VolumeKeymapItem = {
          type: VolumeKeymapTab.SECRET_MAP,
          selected: item.secret.secretName,
          volumeName: item.name,
          items: item.secret.items || [],
          volumeMounts: getVolumeMounts(item.name),
        };
        return result;
      }
    })
    ?.filter(Boolean) as VolumeItem[]; // 过滤可能的 undefined
};
